import React, { useEffect, useState, useMemo, useRef } from "react";
import { Card } from "flowbite-react";
import NavBar from "../NavBar";
import { useAuth } from "../../contexts/AuthContext";
import { db } from "../firebase";
import { toast } from "react-toastify";
import {
  doc,
  getDoc,
  onSnapshot,
  collection,
  addDoc,
  query,
  orderBy,
  updateDoc,
  writeBatch,
  getDocs,
} from "firebase/firestore";
import { fetchSFUCourseDetails, fetchSFUSections } from "../utils/fetchSFUCourse";
import BreaksSection from "./BuildSchedule/BreaksSection";
import CourseSection from "./BuildSchedule/CourseSection";
import {
  computeScheduleCombinations,
  parseTimeGeneric,
  generateDetailedSuggestions,
  getCourseKey,
} from "./BuildSchedule/scheduleAlgorithm";
import { Link } from "react-router-dom";
import TimeTable from "./TimeTable";
import SchedulePreferences from "./BuildSchedule/SchedulePreferences";
import MiniScheduleModal from "./BuildSchedule/MiniScheduleModal";
import ShoppingCart from "./ShoppingCart/ShoppingCart";
import { ScorePopover, getScoreBreakdown } from "./BuildSchedule/ScorePopover";
import CurrentSchedule from "./CurrentSchedule/CurrentSchedule";
import SectionsDrawer from "./BuildSchedule/SectionsDrawer";
import CourseSidebar from "./BuildSchedule/CourseSidebar";

// Initialize local storage caches.
const getInitialCourseCache = () => {
  const stored = localStorage.getItem("scheduleCourseCache");
  return stored ? JSON.parse(stored) : {};
};

const getInitialBreaksCache = () => {
  const stored = localStorage.getItem("scheduleBreaksCache");
  return stored ? JSON.parse(stored) : [];
};

function SchedulePage() {
  const { currentUser } = useAuth();
  const parseTime = parseTimeGeneric;
  const formatHour = (hour) => {
    const period = hour < 12 ? "AM" : "PM";
    const adjusted = hour % 12 === 0 ? 12 : hour % 12;
    return `${adjusted}:00 ${period}`;
  };
  const formatTime = (timeStr) => {
    const minutes = parseTime(timeStr);
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    const period = h < 12 ? "AM" : "PM";
    const adjusted = h % 12 === 0 ? 12 : h % 12;
    const mm = m.toString().padStart(2, "0");
    return `${adjusted}:${mm} ${period}`;
  };

  // ------------------ State Variables ----------------------
  const [ignoreDuplicateSections, setIgnoreDuplicateSections] = useState(false);
  const [activeTab, setActiveTab] = useState("buildSchedule");
  const [courseStatus, setCourseStatus] = useState("all");
  const [campus, setCampus] = useState("all");
  const [instructionMode, setInstructionMode] = useState("all");
  const [availableCampuses, setAvailableCampuses] = useState([]);
  const [availableInstructionModes, setAvailableInstructionModes] = useState([]);
  const [userData, setUserData] = useState(null);
  const [allSemesters, setAllSemesters] = useState([]);
  const [selectedSemester, setSelectedSemester] = useState(null);
  const [plannedCourses, setPlannedCourses] = useState([]);
  const [activeCourses, setActiveCourses] = useState([]);
  const [allCourseSchedules, setAllCourseSchedules] = useState([]);
  const [courseSchedules, setCourseSchedules] = useState([]);
  const [combinations, setCombinations] = useState([]);
  const [combinationIndex, setCombinationIndex] = useState(0);
  const [isComputing, setIsComputing] = useState(false);
  const [hasComputed, setHasComputed] = useState(false);
  const [breaks, setBreaks] = useState(getInitialBreaksCache());
  const [preferences, setPreferences] = useState({
    desiredStart: "09:00",
    desiredEnd: "17:00",
    lunchStart: "12:00",
    lunchEnd: "13:00",
    afterClassBreakEnabled: false,
    afterClassBreakDuration: "10",
    enforceStartEnd: false,
    enforceLunch: false,
    weightStart: 3,
    weightEnd: 3,
    weightLunch: 3,
    weightAfterClassBreak: 3,
  });
  const [ignoreRecitations, setIgnoreRecitations] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [lastComputedState, setLastComputedState] = useState(null);
  const [showMiniModal, setShowMiniModal] = useState(false);
  const [shoppingCart, setShoppingCart] = useState([]);
  const [showScorePopover, setShowScorePopover] = useState(false);
  const [cartIndex, setCartIndex] = useState(0);
  const [loadingPlannedCourses, setLoadingPlannedCourses] = useState(true);
  const [lastAddedScheduleId, setLastAddedScheduleId] = useState("");

  // NEW: State for user-selected sections.
  const [selectedSections, setSelectedSections] = useState({});

  // NEW: State for the section selection drawer.
  const [sectionDrawerData, setSectionDrawerData] = useState(null);
  const [isSectionDrawerOpen, setIsSectionDrawerOpen] = useState(false);

  // Use LocalStorage to cache course data.
  const [courseCache, setCourseCache] = useState(getInitialCourseCache);

  // New state for a 300ms delay on the loading spinner.
  const [minDelayPassed, setMinDelayPassed] = useState(false);

  // NEW: State for interactive sidebar (opens when a course is clicked on the timetable)
  const [sidebarCourse, setSidebarCourse] = useState(null);

  const noDataToastShown = useRef(new Set());
  const badMeetingToastShown = useRef(new Set());

  useEffect(() => {
    localStorage.setItem("scheduleCourseCache", JSON.stringify(courseCache));
  }, [courseCache]);

  useEffect(() => {
    localStorage.setItem("scheduleBreaksCache", JSON.stringify(breaks));
  }, [breaks]);

  const scheduleRef = useRef(null);
  const scoreButtonRef = useRef(null);

  useEffect(() => {
    if (hasComputed && scheduleRef.current) {
      scheduleRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [hasComputed]);

  useEffect(() => {
    if (userData && !loadingPlannedCourses) {
      const timer = setTimeout(() => {
        setMinDelayPassed(true);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setMinDelayPassed(false);
    }
  }, [userData, loadingPlannedCourses]);

  useEffect(() => {
    if (!hasComputed || !lastComputedState) {
      setIsDirty(false);
      return;
    }
    const currentState = { activeCourses, breaks, preferences, ignoreRecitations, ignoreDuplicateSections, selectedSections };
    if (JSON.stringify(currentState) !== JSON.stringify(lastComputedState)) {
      setIsDirty(true);
    } else {
      setIsDirty(false);
    }
  }, [activeCourses, breaks, preferences, ignoreRecitations, ignoreDuplicateSections, selectedSections, hasComputed, lastComputedState]);

  useEffect(() => {
    if (currentUser) {
      const userDocRef = doc(db, "users", currentUser.uid);
      getDoc(userDocRef)
        .then((docSnap) => {
          if (docSnap.exists()) {
            setUserData(docSnap.data());
          } else {
            console.error("User document does not exist for uid:", currentUser.uid);
          }
        })
        .catch((error) => {
          console.error("Error fetching user document:", error);
        });
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      const cartCollectionRef = collection(db, "users", currentUser.uid, "scheduleCart");
      const q = query(cartCollectionRef, orderBy("createdAt"));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const cartItems = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setShoppingCart(cartItems);
      });
      return () => unsubscribe();
    }
  }, [currentUser]);

  useEffect(() => {
    if (lastAddedScheduleId) {
      const index = shoppingCart.findIndex((item) => item.id === lastAddedScheduleId);
      if (index !== -1) {
        setCartIndex(index);
        setLastAddedScheduleId("");
      }
    }
  }, [shoppingCart, lastAddedScheduleId]);

  // Use only the primary plan for schedule data.
  useEffect(() => {
    if (!userData || !userData.schedules) return;
    const primaryTab = userData.primaryTab || Object.keys(userData.schedules)[0];
    const planData = userData.schedules[primaryTab];
    if (!planData) return;
    const semesters = [];
    const TERM_ORDER = ["Fall", "Spring", "Summer"];
    const numYears = planData.numYears || 4;
    for (let y = 1; y <= numYears; y++) {
      const yearKey = `year${y}`;
      const yearData = planData[yearKey];
      if (!yearData) continue;
      Object.entries(yearData).forEach(([termKey, termData]) => {
        if (termData && Array.isArray(termData.courses) && termData.courses.length > 0) {
          semesters.push({
            planName: primaryTab,
            yearIndex: y,
            sfuYear: 2023 + (y - 1),
            sfuTerm: termKey.toLowerCase(),
            term: termKey,
            label: `${primaryTab} - Year ${y} (${termKey})`,
          });
        }
      });
    }
    semesters.sort((a, b) => {
      if (a.yearIndex !== b.yearIndex) return a.yearIndex - b.yearIndex;
      const indexA = TERM_ORDER.indexOf(a.term);
      const indexB = TERM_ORDER.indexOf(b.term);
      return indexA - indexB;
    });
    setAllSemesters(semesters);
    if (semesters.length > 0) {
      setSelectedSemester(semesters[0]);
    }
  }, [userData]);

  // Use the selected term from the primary plan.
  useEffect(() => {
    if (!selectedSemester || !userData || !userData.schedules) {
      setPlannedCourses([]);
      setActiveCourses([]);
      setLoadingPlannedCourses(false);
      return;
    }
    const { planName, yearIndex, term } = selectedSemester;
    const planData = userData.schedules[planName];
    if (!planData || !planData[`year${yearIndex}`]) {
      setPlannedCourses([]);
      setActiveCourses([]);
      setLoadingPlannedCourses(false);
      return;
    }
    const semesterData = planData[`year${yearIndex}`][term];
    if (!semesterData || !semesterData.courses) {
      setPlannedCourses([]);
      setActiveCourses([]);
      setLoadingPlannedCourses(false);
      return;
    }
    const courses = semesterData.courses.map((courseId) => {
      let courseInfoStr = courseId;
      if (courseId.includes(" - ")) {
        const parts = courseId.split(" - ");
        courseInfoStr = parts[parts.length - 1].trim();
      }
      const tokens = courseInfoStr.split(" ");
      return {
        dept: (tokens[0] || "").toLowerCase(),
        number: tokens[1] || "000",
        section: tokens[2] ? tokens[2].toLowerCase() : "",
      };
    });
    setPlannedCourses(courses);
    setActiveCourses(courses);
    setLoadingPlannedCourses(false);
  }, [selectedSemester, userData]);

  // Ensure a minimum delay for the loading spinner.
  useEffect(() => {
    if (userData && !loadingPlannedCourses) {
      const timer = setTimeout(() => {
        setMinDelayPassed(true);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setMinDelayPassed(false);
    }
  }, [userData, loadingPlannedCourses]);

  // ------------------ NEW HELPER FUNCTION ------------------
  // Returns a friendlier course display name (e.g. "CA 127" instead of "CA_127_DEFAULT").
  function getCourseDisplayName(course) {
    const dept = course.dept ? course.dept.toUpperCase() : "";
    const number = course.number || "";
    if (course.section && course.section.trim() !== "" && course.section.toUpperCase() !== "DEFAULT") {
      return `${dept} ${number} ${course.section.toUpperCase()}`;
    }
    return `${dept} ${number}`;
  }
  // -----------------------------------------------------------

  // Helper function to return the schedule for a course.
  function getCourseSchedule(course) {
    if (course.sfuData && Array.isArray(course.sfuData.courseSchedule) && course.sfuData.courseSchedule.length > 0) {
      return course.sfuData.courseSchedule;
    }
    if (course.availableSections && course.availableSections.length > 0) {
      if (
        course.availableSections[0].sfuData &&
        Array.isArray(course.availableSections[0].sfuData.courseSchedule) &&
        course.availableSections[0].sfuData.courseSchedule.length > 0
      ) {
        return course.availableSections[0].sfuData.courseSchedule;
      }
    }
    return [];
  }

  // --- UPDATED analyzeConflicts ---
  // Now, for course-to-course conflicts, once a conflict is found between a pair of courses,
  // we break out so that each conflicting pair is reported only once.
  function analyzeConflicts(courseSchedules, breaks, preferences, formatTime, parseTime) {
    const conflicts = [];

    // Check course-to-course conflicts.
    for (let i = 0; i < courseSchedules.length; i++) {
        const courseA = courseSchedules[i];
        const scheduleA = getCourseSchedule(courseA);
        if (scheduleA.length === 0) continue;
        for (let j = i + 1; j < courseSchedules.length; j++) {
            const courseB = courseSchedules[j];
            const scheduleB = getCourseSchedule(courseB);
            if (scheduleB.length === 0) continue;
            let conflictFound = false;
            for (let m1 of scheduleA) {
                if (conflictFound) break;
                for (let m2 of scheduleB) {
                    const days1 = m1.days ? m1.days.split(",").map((d) => d.trim()) : [];
                    const days2 = m2.days ? m2.days.split(",").map((d) => d.trim()) : [];
                    const commonDays = days1.filter((day) => days2.includes(day));
                    if (commonDays.length > 0) {
                        const start1 = parseTime(m1.startTime || "0:00");
                        const end1 = parseTime(m1.endTime || "0:00");
                        const start2 = parseTime(m2.startTime || "0:00");
                        const end2 = parseTime(m2.endTime || "0:00");
                        if (start1 < end2 && end1 > start2) {
                            conflicts.push({
                                type: "courseConflict",
                                courseA,
                                meetingA: m1,
                                courseB,
                                meetingB: m2,
                                commonDays,
                                message: `${getCourseDisplayName(courseA)} and ${getCourseDisplayName(courseB)} cannot be scheduled together.`,
                            });
                            conflictFound = true;
                            break;
                        }
                    }
                }
            }
        }
    }

    // Check conflicts with breaks.
    courseSchedules.forEach((course) => {
        const schedule = getCourseSchedule(course);
        schedule.forEach((meeting) => {
            const days = meeting.days ? meeting.days.split(",").map((d) => d.trim()) : [];
            const start = parseTime(meeting.startTime || "0:00");
            const end = parseTime(meeting.endTime || "0:00");
            breaks.forEach((bk, breakIndex) => {
                if (bk.selected && bk.days && bk.days.some((day) => days.includes(day))) {
                    const breakStart = parseTime(bk.start);
                    const breakEnd = parseTime(bk.end);
                    if (start < breakEnd && end > breakStart) {
                        conflicts.push({
                            type: "breakConflict",
                            course,
                            meeting,
                            break: bk,
                            breakIndex,
                            message: `${getCourseDisplayName(course)} conflicts with break "${bk.name}".`,
                        });
                    }
                }
            });
        });
    });

    // Check preference conflicts if enforce start/end is enabled.
    if (preferences.enforceStartEnd) {
        courseSchedules.forEach((course) => {
            const schedule = getCourseSchedule(course);
            schedule.forEach((meeting) => {
                const start = parseTime(meeting.startTime || "0:00");
                const end = parseTime(meeting.endTime || "0:00");
                const desiredStart = parseTime(preferences.desiredStart || "08:00");
                const desiredEnd = parseTime(preferences.desiredEnd || "17:00");
                if (start < desiredStart) {
                    conflicts.push({
                        type: "preferenceConflict",
                        course,
                        meeting,
                        conflictType: "start",
                        message: `${getCourseDisplayName(course)} starts before the desired start time.`,
                    });
                }
                if (end > desiredEnd) {
                    conflicts.push({
                        type: "preferenceConflict",
                        course,
                        meeting,
                        conflictType: "end",
                        message: `${getCourseDisplayName(course)} ends after the desired end time.`,
                    });
                }
            });
        });
    }
    return conflicts;
}

  async function fetchSchedulesForCourse(course) {
    const key = getCourseCacheKey(course);
    if (courseCache[key]) {
      return courseCache[key];
    }
    const TERMS_TO_TRY = [
      { year: "registration", term: "registration" },
      { year: "2025", term: "spring" },
      { year: "2024", term: "fall" },
      { year: "2024", term: "spring" },
    ];
    for (const { year, term } of TERMS_TO_TRY) {
      try {
        if (course.section) {
          const data = await fetchSFUCourseDetails(year, term, course.dept, course.number, course.section);
          if (data) {
            if (data.courseSchedule && Array.isArray(data.courseSchedule)) {
              const originalLength = data.courseSchedule.length;
              data.courseSchedule = filterBadMeetings(data.courseSchedule);
              if (
                data.courseSchedule.length < originalLength &&
                !badMeetingToastShown.current.has(key)
              ) {
                toast.error(
                  `Course ${course.dept.toUpperCase()} ${course.number} has extra weekend meeting entries that have been filtered out for scheduling.`
                );
                badMeetingToastShown.current.add(key);
              }
            }
            const result = { ...course, sfuData: data, fetchedTerm: term, sfuYear: year };
            setCourseCache((prev) => ({ ...prev, [key]: result }));
            return result;
          }
        } else {
          const sections = await fetchSFUSections(year, term, course.dept, course.number);
          if (sections && sections.length > 0) {
            const availableSections = await Promise.all(
              sections.map(async (secItem) => {
                const sectionCode = secItem.value;
                try {
                  const data = await fetchSFUCourseDetails(year, term, course.dept, course.number, sectionCode);
                  if (data && data.courseSchedule && Array.isArray(data.courseSchedule)) {
                    const originalLength = data.courseSchedule.length;
                    data.courseSchedule = filterBadMeetings(data.courseSchedule);
                    const combinedKey = key + sectionCode;
                    if (
                      data.courseSchedule.length < originalLength &&
                      !badMeetingToastShown.current.has(combinedKey)
                    ) {
                      toast.error(
                        `Course ${course.dept.toUpperCase()} ${course.number} section ${sectionCode.toUpperCase()} has extra weekend meeting entries that have been filtered out for scheduling.`
                      );
                      badMeetingToastShown.current.add(combinedKey);
                    }
                  }
                  return { section: sectionCode, sfuData: data, ...secItem, fetchedTerm: term };
                } catch (error) {
                  console.error(
                    `Error fetching details for ${course.dept.toUpperCase()} ${course.number} section ${sectionCode} in ${year}/${term}`,
                    error
                  );
                  return { section: sectionCode, sfuData: null, ...secItem, fetchedTerm: term };
                }
              })
            );
            const result = { ...course, availableSections, sfuYear: year, fetchedTerm: term };
            setCourseCache((prev) => ({ ...prev, [key]: result }));
            return result;
          }
        }
      } catch (error) {
        console.error(`Fetch attempt failed for ${course.dept.toUpperCase()} ${course.number} in ${year}/${term}`, error);
      }
    }
    console.warn(`All attempts failed for ${course.dept.toUpperCase()} ${course.number}`);
    if (!noDataToastShown.current.has(key)) {
      toast.info(
        `No API data for ${course.dept.toUpperCase()} ${course.number}. Check back later when it's updated for the registration period.`
      );
      noDataToastShown.current.add(key);
    }
    return { ...course };
  }

  function filterBadMeetings(schedule) {
    return schedule.filter((meeting) => {
      if (!meeting.days) return true;
      const days = meeting.days.split(",").map((d) => d.trim());
      if (
        days.some((day) => day === "Sa" || day === "Su") &&
        meeting.startTime === "8:00" &&
        meeting.endTime === "23:59"
      ) {
        return false;
      }
      return true;
    });
  }

  useEffect(() => {
    async function fetchAllSchedules() {
      if (plannedCourses.length === 0 || !selectedSemester) {
        setAllCourseSchedules([]);
        setLoadingPlannedCourses(false);
        return;
      }
      const results = await Promise.all(plannedCourses.map((course) => fetchSchedulesForCourse(course)));
      setAllCourseSchedules(results);
      setCombinations([]);
      setHasComputed(false);
      setIsDirty(false);
      setLastComputedState(null);
    }
    fetchAllSchedules();
  }, [plannedCourses, selectedSemester, courseCache]);

  useEffect(() => {
    const campusesSet = new Set();
    const modesSet = new Set();
    allCourseSchedules.forEach((schedule) => {
      if (schedule.sfuData && schedule.sfuData.courseSchedule) {
        schedule.sfuData.courseSchedule.forEach((meeting) => {
          if (meeting.campus) campusesSet.add(meeting.campus);
        });
        if (schedule.sfuData.deliveryMethod) modesSet.add(schedule.sfuData.deliveryMethod);
      }
      if (schedule.availableSections) {
        schedule.availableSections.forEach((sec) => {
          if (sec.sfuData && sec.sfuData.courseSchedule) {
            sec.sfuData.courseSchedule.forEach((meeting) => {
              if (meeting.campus) campusesSet.add(meeting.campus);
            });
            if (sec.sfuData.deliveryMethod) modesSet.add(sec.sfuData.deliveryMethod);
          }
        });
      }
    });
    setAvailableCampuses(Array.from(campusesSet).sort());
    setAvailableInstructionModes(Array.from(modesSet).sort());
  }, [allCourseSchedules]);

  useEffect(() => {
    const filtered = allCourseSchedules.filter((schedule) => {
      const isSelectedCourse = activeCourses.some(
        (ac) => ac.dept === schedule.dept && ac.number === schedule.number && ac.section === schedule.section
      );
      if (!isSelectedCourse) return false;
      let campusMatch = true;
      if (campus !== "all") {
        if (schedule.sfuData && schedule.sfuData.courseSchedule) {
          campusMatch = schedule.sfuData.courseSchedule.some(
            (meeting) => meeting.campus && meeting.campus.toLowerCase() === campus.toLowerCase()
          );
        } else if (schedule.availableSections) {
          campusMatch = schedule.availableSections.some((sec) => {
            if (sec.sfuData && sec.sfuData.courseSchedule) {
              return sec.sfuData.courseSchedule.some(
                (meeting) => meeting.campus && meeting.campus.toLowerCase() === campus.toLowerCase()
              );
            }
            return false;
          });
        }
      }
      let instructionMatch = true;
      if (instructionMode !== "all") {
        if (schedule.sfuData && schedule.sfuData.deliveryMethod) {
          instructionMatch = schedule.sfuData.deliveryMethod.toLowerCase() === instructionMode.toLowerCase();
        } else if (schedule.availableSections) {
          instructionMatch = schedule.availableSections.some((sec) => {
            return (
              sec.sfuData &&
              sec.sfuData.deliveryMethod &&
              sec.sfuData.deliveryMethod.toLowerCase() === instructionMode.toLowerCase()
            );
          });
        }
      }
      let statusMatch = true;
      if (courseStatus === "open") {
        const singleFilled =
          schedule.sfuData && !schedule.sfuData.errorMessage && schedule.sfuData.courseSchedule?.length > 0;
        const multiFilled = schedule.availableSections && schedule.availableSections.length > 0;
        statusMatch = singleFilled || multiFilled;
      }
      return campusMatch && instructionMatch && statusMatch;
    });
    setCourseSchedules(filtered);
  }, [activeCourses, allCourseSchedules, campus, instructionMode, courseStatus]);

  function fixBreakConflict(breakIndex) {
    const updated = breaks.filter((_, i) => i !== breakIndex);
    setBreaks(updated);
    handleComputeCombinations();
    setIsDirty(false);
  }
  function fixCourseConflict(course) {
    const exists = activeCourses.some(
      (ac) => ac.dept === course.dept && ac.number === course.number && ac.section === course.section
    );
    if (exists) {
      toggleCourse(course);
    }
    handleComputeCombinations();
    setIsDirty(false);
  }
  function fixPreferenceConflict(conflict) {
    if (conflict.conflictType === "start") {
      setPreferences({ ...preferences, desiredStart: conflict.meeting.startTime });
    } else if (conflict.conflictType === "end") {
      setPreferences({ ...preferences, desiredEnd: conflict.meeting.endTime });
    }
    handleComputeCombinations();
    setIsDirty(false);
  }

  async function handleComputeCombinations() {
    setHasComputed(false);
    setIsComputing(true);
    let computed = await computeScheduleCombinations(
      courseSchedules,
      breaks.filter((bk) => bk.selected),
      { ignoreRecitations, preferences },
      selectedSections
    );
    if (ignoreDuplicateSections) {
      const deduped = [];
      const seenKeys = new Set();
      computed.forEach((combo) => {
        const optionKeys = combo.combination.map((opt) => {
          if (opt.sfuData && opt.sfuData.courseSchedule) {
            const meetings = opt.sfuData.courseSchedule
              .map((m) => `${m.startTime}-${m.endTime}-${m.days}`)
              .sort()
              .join(",");
            return `${opt.dept}-${opt.number}-${meetings}`;
          }
          return `${opt.dept}-${opt.number}`;
        });
        const key = optionKeys.join("|");
        if (!seenKeys.has(key)) {
          seenKeys.add(key);
          deduped.push(combo);
        }
      });
      computed = deduped;
    }
    setCombinations(computed);
    setCombinationIndex(0);
    setIsComputing(false);
    setIsDirty(false);
    setLastComputedState({
      activeCourses: JSON.parse(JSON.stringify(activeCourses)),
      breaks: JSON.parse(JSON.stringify(breaks)),
      preferences: JSON.parse(JSON.stringify(preferences)),
      ignoreRecitations: ignoreRecitations,
      ignoreDuplicateSections: ignoreDuplicateSections,
      selectedSections: JSON.parse(JSON.stringify(selectedSections)),
    });
    setHasComputed(true);
  }

  const toggleCourse = (course) => {
    const exists = activeCourses.some(
      (ac) => ac.dept === course.dept && ac.number === course.number && ac.section === course.section
    );
    if (exists) {
      setActiveCourses(
        activeCourses.filter(
          (ac) => !(ac.dept === course.dept && ac.number === course.number && ac.section === course.section)
        )
      );
    } else {
      setActiveCourses([...activeCourses, course]);
    }
  };

  const selectAllCourses = (checked) => {
    setActiveCourses(checked ? plannedCourses : []);
  };

  const currentCombination = combinations[combinationIndex]?.combination || [];
  const currentScore = combinations[combinationIndex]?.score;

  const addCurrentScheduleToCart = async () => {
    if (!currentCombination || currentCombination.length === 0) {
      return;
    }
    const scheduleToAdd = {
      combination: currentCombination,
      score: currentScore,
      breaks: breaks,
      createdAt: new Date().toISOString(),
    };
    if (currentUser) {
      try {
        const cartCollectionRef = collection(db, "users", currentUser.uid, "scheduleCart");
        const docRef = await addDoc(cartCollectionRef, scheduleToAdd);
        setLastAddedScheduleId(docRef.id);
        setActiveTab("shoppingCart");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (error) {
        console.error("Error adding schedule to cart in Firestore", error);
      }
    } else {
      setShoppingCart((prev) => {
        const newCart = [...prev, { ...scheduleToAdd, id: `${Date.now()}-${Math.random()}` }];
        setCartIndex(newCart.length - 1);
        return newCart;
      });
      setActiveTab("shoppingCart");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const scoreBreakdown = useMemo(() => {
    if (currentCombination.length === 0) return [];
    return getScoreBreakdown(currentCombination, preferences, parseTime);
  }, [currentCombination, preferences, parseTime]);

  const hexToRgb = (hex) => {
    hex = hex.replace(/^#/, "");
    if (hex.length === 3) {
      hex = hex.split("").map((c) => c + c).join("");
    }
    const intVal = parseInt(hex, 16);
    return [(intVal >> 16) & 255, (intVal >> 8) & 255, intVal & 255];
  };

  const rgbToHex = (r, g, b) => {
    const toHex = (c) => {
      const hex = c.toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    };
    return "#" + toHex(r) + toHex(g) + toHex(b);
  };

  const interpolateColor = (color1, color2, factor) => {
    const rgb1 = hexToRgb(color1);
    const rgb2 = hexToRgb(color2);
    const result = rgb1.map((c, i) => Math.round(c + factor * (rgb2[i] - c)));
    return rgbToHex(...result);
  };

  const getColorsForScore = (score) => {
    const redBg = "#fee2e2";
    const yellowBg = "#fef9c3";
    const greenBg = "#d1fae5";
    const redText = "#b91c1c";
    const yellowText = "#92400e";
    const greenText = "#047857";

    let bgColor, textColor;
    if (score <= 50) {
      const factor = score / 50;
      bgColor = interpolateColor(redBg, yellowBg, factor);
      textColor = interpolateColor(redText, yellowText, factor);
    } else {
      const factor = (score - 50) / 50;
      bgColor = interpolateColor(yellowBg, greenBg, factor);
      textColor = interpolateColor(yellowText, greenText, factor);
    }
    return { bgColor, textColor };
  };

  const openSectionDrawer = (course, sections) => {
    setSectionDrawerData({ course, sections });
    setIsSectionDrawerOpen(true);
  };

  const handleCourseClickFromTimeTable = (course) => {
    const key = getCourseKey(course);
    if (selectedSections[key]) {
      setSidebarCourse(selectedSections[key]);
    } else {
      const fetched = allCourseSchedules.find(
        (cs) =>
          cs.dept === course.dept &&
          cs.number === course.number &&
          cs.section === course.section
      );
      if (fetched && fetched.availableSections) {
        setSidebarCourse({ ...course, sfuData: fetched.sfuData, availableSections: fetched.availableSections });
      } else {
        setSidebarCourse(course);
      }
    }
  };

  const handleSelectAlternative = (alternative) => {
    const index = combinations.findIndex((combo) => combo === alternative);
    if (index !== -1) {
      setCombinationIndex(index);
    }
  };

  if (!userData || loadingPlannedCourses) {
    return (
      <div role="status" className="flex justify-center items-center min-h-screen">
        <svg
          aria-hidden="true"
          className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 
               100.591 50 100.591C22.3858 100.591 
               0 78.2051 0 50.5908C0 22.9766 22.3858 
               0.59082 50 0.59082C77.6142 0.59082 100 
               22.9766 100 50.5908ZM9.08144 50.5908C9.08144 
               73.1895 27.4013 91.5094 50 91.5094C72.5987 
               91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 
               27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 
               9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 
               35.9116 97.0079 33.5539C95.2932 28.8227 92.871 
               24.3692 89.8167 20.348C85.8452 15.1192 80.8826 
               10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 
               1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 
               0.446843 41.7345 1.27873C39.2613 1.69328 37.813 
               4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 
               10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 
               9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 
               12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 
               21.5619 82.5849 25.841C84.9175 28.9121 86.7997 
               32.2913 88.1811 35.8758C89.083 38.2158 91.5421 
               39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span className="sr-only">Loading...</span>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-white">
      <NavBar />
      <div className="container mx-auto mt-10 mb-6 flex transition-all duration-300" style={{ gap: "1rem" }}>
        <div className="transition-all duration-300 flex-1" style={{ transform: sidebarCourse ? "translateX(-10rem)" : "translateX(0)" }}>
          <div className="pt-10 mb-3 border-b border-gray-200">
            <ul className="flex flex-wrap -mb-px text-sm font-medium text-center" role="tablist">
              <li className="mr-2" role="presentation">
                <button
                  className={`inline-block p-4 border-b-2 rounded-t-lg ${
                    activeTab === "buildSchedule"
                      ? "text-blue-600 border-blue-600"
                      : "hover:text-gray-600 hover:border-gray-300"
                  }`}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "buildSchedule"}
                  onClick={() => setActiveTab("buildSchedule")}
                >
                  Build Schedule
                </button>
              </li>
              <li className="mr-2" role="presentation">
                <button
                  className={`inline-block p-4 border-b-2 rounded-t-lg ${
                    activeTab === "shoppingCart"
                      ? "text-blue-600 border-blue-600"
                      : "hover:text-gray-600 hover:border-gray-300"
                  }`}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "shoppingCart"}
                  onClick={() => setActiveTab("shoppingCart")}
                >
                  Shopping Cart
                </button>
              </li>
              <li role="presentation">
                <button
                  className={`inline-block p-4 border-b-2 rounded-t-lg ${
                    activeTab === "currentSchedule"
                      ? "text-blue-600 border-blue-600"
                      : "hover:text-gray-600 hover:border-gray-300"
                  }`}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "currentSchedule"}
                  onClick={() => setActiveTab("currentSchedule")}
                >
                  Current Schedule
                </button>
              </li>
            </ul>
          </div>

          {activeTab === "buildSchedule" && (
            <>
              {!selectedSemester ? (
                <div
                  id="alert-additional-content-5"
                  className="p-4 border border-gray-300 rounded-lg bg-gray-50 dark:border-gray-600 dark:bg-gray-800"
                  role="alert"
                >
                  <div className="flex items-center">
                    <svg
                      className="shrink-0 w-4 h-4 me-2 dark:text-gray-300"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z" />
                    </svg>
                    <span className="sr-only">Info</span>
                    <h3 className="text-lg font-medium text-gray-800 dark:text-gray-300">
                      No Courses Added
                    </h3>
                  </div>
                  <div className="mt-2 mb-4 text-sm text-gray-800 dark:text-gray-300">
                    It looks like you haven't added any classes to your student plan. Please add courses to start building your schedule.
                  </div>
                  <div className="flex">
                    <Link
                      to="/plan"
                      className="text-white bg-gray-700 hover:bg-gray-800 focus:ring-4 focus:outline-none focus:ring-gray-300 font-medium rounded-lg text-xs px-3 py-1.5 inline-flex items-center dark:bg-gray-600 dark:hover:bg-gray-500 dark:focus:ring-gray-800"
                    >
                      <svg
                        className="me-2 h-3 w-3 dark:text-gray-300"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                        viewBox="0 0 20 14"
                      >
                        <path d="M10 0C4.612 0 0 5.336 0 7c0 1.742 3.546 7 10 7 6.454 0 10-5.258 10-7 0-1.664-4.612-7-10-7Zm0 10a3 3 0 1 1 0-6 3 3 0 0 1 0 6Z" />
                      </svg>
                      Add Courses
                    </Link>
                  </div>
                </div>
              ) : (
                <>
                  <Card className="mb-2">
                    <div className="flex items-center">
                      <div className="flex flex-row items-center space-x-5">
                        <div className="flex items-center">
                          <label className="mr-1 text-sm font-medium text-gray-700">Term:</label>
                          <select
                            value={selectedSemester ? selectedSemester.label : ""}
                            onChange={(e) => {
                              const chosenLabel = e.target.value;
                              const newSem = allSemesters.find((s) => s.label === chosenLabel);
                              setSelectedSemester(newSem || null);
                            }}
                            className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                          >
                            {allSemesters.map((sem) => (
                              <option key={sem.label} value={sem.label}>
                                {sem.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="flex items-center">
                          <label className="mr-1 text-sm font-medium text-gray-700">
                            Course Status:
                          </label>
                          <select
                            value={courseStatus}
                            onChange={(e) => setCourseStatus(e.target.value)}
                            className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                          >
                            <option value="all">All Classes</option>
                            <option value="open">Open Classes Only</option>
                          </select>
                        </div>
                        <div className="flex items-center">
                          <label className="mr-1 text-sm font-medium text-gray-700">Campus:</label>
                          <select
                            value={campus}
                            onChange={(e) => setCampus(e.target.value)}
                            className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                          >
                            <option value="all">All Campuses</option>
                            {availableCampuses.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="flex items-center">
                          <label className="mr-1 text-sm font-medium text-gray-700">
                            Instruction Mode:
                          </label>
                          <select
                            value={instructionMode}
                            onChange={(e) => setInstructionMode(e.target.value)}
                            className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                          >
                            <option value="all">All Modes</option>
                            {availableInstructionModes.map((mode) => (
                              <option key={mode} value={mode}>
                                {mode}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </Card>

                  <div className="flex flex-col md:flex-row gap-2">
                    <Card className="w-full md:w-1/2">
                      <CourseSection
                        plannedCourses={plannedCourses}
                        activeCourses={activeCourses}
                        allCourseSchedules={allCourseSchedules}
                        isComputing={isComputing}
                        computeCombinations={handleComputeCombinations}
                        combinations={combinations}
                        combinationIndex={combinationIndex}
                        setCombinationIndex={setCombinationIndex}
                        onToggleCourse={toggleCourse}
                        onSelectAllCourses={selectAllCourses}
                        hasComputed={hasComputed}
                        isDirty={isDirty}
                        selectedSections={selectedSections}
                        setSelectedSections={setSelectedSections}
                        openSectionDrawer={openSectionDrawer}
                      />
                    </Card>
                    <Card className="w-full md:w-1/2">
                      <div>
                        <BreaksSection
                          breaks={breaks}
                          setBreaks={setBreaks}
                          formatTime={formatTime}
                        />
                      </div>
                      <hr />
                      <SchedulePreferences
                        preferences={preferences}
                        setPreferences={setPreferences}
                        ignoreRecitations={ignoreRecitations}
                        setIgnoreRecitations={setIgnoreRecitations}
                        ignoreDuplicateSections={ignoreDuplicateSections}
                        setIgnoreDuplicateSections={setIgnoreDuplicateSections}
                      />
                    </Card>
                  </div>

                  {hasComputed && plannedCourses.length > 0 && (
                    <div ref={scheduleRef}>
                      <Card className="mt-2">
                        {combinations.length > 0 && (
                          <div className="grid grid-cols-3 items-center">
                            <div className="inline-block relative">
                              {currentScore !== null && (
                                <button
                                  ref={scoreButtonRef}
                                  onMouseEnter={() => setShowScorePopover(true)}
                                  onMouseLeave={() => setShowScorePopover(false)}
                                  data-popover-target="popover-no-arrow"
                                  style={{
                                    backgroundColor: getColorsForScore(currentScore).bgColor,
                                    color: getColorsForScore(currentScore).textColor,
                                  }}
                                  className="text-lg font-semibold rounded-lg px-2 py-1 mr-2 flex items-center focus:outline-none focus:ring-2 focus:ring-offset-2"
                                >
                                  Score: {currentScore}/100
                                </button>
                              )}
                              <ScorePopover
                                anchorRef={scoreButtonRef}
                                visible={showScorePopover}
                                combination={currentCombination}
                                preferences={preferences}
                                parseTime={parseTime}
                              />
                            </div>
                            <div className="flex items-center justify-center space-x-2">
                              <button
                                onClick={() =>
                                  setCombinationIndex((prev) => (prev === 0 ? combinations.length - 1 : prev - 1))
                                }
                                type="button"
                                className="p-2 text-gray-900 focus:outline-none bg-white rounded-full border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700"
                                aria-label="Previous combination"
                              >
                                <svg
                                  aria-hidden="true"
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth={2}
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                </svg>
                              </button>
                              <span className="text-sm text-gray-600">
                                Combination {combinationIndex + 1} of {combinations.length}
                              </span>
                              <button
                                onClick={() =>
                                  setCombinationIndex((prev) => (prev === combinations.length - 1 ? 0 : prev + 1))
                                }
                                type="button"
                                className="p-2 text-gray-900 focus:outline-none bg-white rounded-full border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700"
                                aria-label="Next combination"
                              >
                                <svg
                                  aria-hidden="true"
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth={2}
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                </svg>
                              </button>
                            </div>
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                className="text-blue-700 hover:text-white border border-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-3 py-2 text-center me-1 mb-2 dark:border-blue-500 dark:text-blue-500 dark:hover:text-white dark:hover:bg-blue-500 dark:focus:ring-blue-800"
                                onClick={() => {
                                  setIsComputing(true);
                                  setShowMiniModal(true);
                                  setTimeout(() => {
                                    setIsComputing(false);
                                  }, 50);
                                }}
                              >
                                View All Schedules
                              </button>
                              <button
                                className="text-green-700 hover:text-white border border-green-700 hover:bg-green-800 focus:ring-4 focus:outline-none focus:ring-green-300 font-medium rounded-lg text-sm px-3 py-2 text-center me-1 mb-2 dark:border-green-500 dark:text-green-500 dark:hover:text-white dark:hover:bg-green-600 dark:focus:ring-green-800"
                                onClick={addCurrentScheduleToCart}
                              >
                                Add to Cart
                              </button>
                            </div>
                          </div>
                        )}
                        {hasComputed && combinations.length === 0 && !isComputing && (
                          <div className="p-4 border border-red-300 bg-red-50">
                            <p className="text-sm text-red-800">
                              No valid schedule combination was found with the selected courses.
                            </p>

                            {/* Collect and display conflict messages and suggestions */}
                            {(() => {
                              const conflictObjects = analyzeConflicts(
                                courseSchedules,
                                breaks,
                                preferences,
                                formatTime,
                                parseTime
                              );
                              const uniqueIssues = Array.from(
                                new Set(conflictObjects.map((c) => c.message))
                              );
                              const suggestions = generateDetailedSuggestions(
                                conflictObjects,
                                courseSchedules
                              );
                              const uniqueSuggestions = Array.from(new Set(suggestions));

                              if (conflictObjects.length === 0) {
                                return null;
                              }

                              return (
                                <div className="mt-4">
                                  <h3 className="font-bold text-red-800">Detected Issues:</h3>
                                  <ul className="list-disc list-inside space-y-2 mt-2">
                                    {uniqueIssues.map((msg, index) => (
                                      <li key={index} className="text-sm text-red-700">
                                        {msg}
                                      </li>
                                    ))}
                                  </ul>
                                  <div className="mt-4">
                                    <h3 className="font-bold text-red-800">Suggestions:</h3>
                                    <ul className="list-disc list-inside space-y-2 mt-2">
                                      {uniqueSuggestions.map((suggestion, idx) => (
                                        <li key={idx} className="text-sm text-red-700">
                                          {suggestion}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        )}
                        <TimeTable
                          currentCombination={currentCombination}
                          breaks={breaks}
                          parseTime={parseTime}
                          formatHour={formatHour}
                          formatTime={formatTime}
                          plannedCourses={plannedCourses}
                          onCourseClick={handleCourseClickFromTimeTable}
                        />
                      </Card>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {activeTab === "shoppingCart" && (
            <ShoppingCart
              shoppingCart={shoppingCart}
              formatTime={formatTime}
              parseTime={parseTime}
              onAddCourses={() => setActiveTab("buildSchedule")}
              currentIndex={cartIndex}
              setCurrentIndex={setCartIndex}
              currentUser={currentUser}
              onCourseClick={handleCourseClickFromTimeTable}
            />
          )}

          {activeTab === "currentSchedule" && <CurrentSchedule />}
        </div>

        {sidebarCourse && (
          <CourseSidebar
            course={sidebarCourse}
            availableSections={sidebarCourse.availableSections || []}
            scheduleCombinations={combinations}
            onClose={() => setSidebarCourse(null)}
            onLockSection={(section) => {
              const key = getCourseCacheKey(sidebarCourse);
              setSelectedSections((prev) => ({
                ...prev,
                [key]: { ...section, dept: sidebarCourse.dept, number: sidebarCourse.number },
              }));
              setSidebarCourse(null);
              handleComputeCombinations();
            }}
            onSelectAlternative={handleSelectAlternative}
          />
        )}
      </div>

      {showMiniModal && (
        <MiniScheduleModal
          open={showMiniModal}
          onClose={() => setShowMiniModal(false)}
          combinations={combinations}
          breaks={breaks}
          parseTime={parseTime}
          plannedCourses={plannedCourses}
          isComputing={isComputing}
          onSelect={(index) => {
            setCombinationIndex(index);
            setShowMiniModal(false);
          }}
        />
      )}

      {isSectionDrawerOpen && sectionDrawerData && (
        <SectionsDrawer
          isOpen={isSectionDrawerOpen}
          onClose={() => setIsSectionDrawerOpen(false)}
          title={`Select Section for ${sectionDrawerData.course.dept.toUpperCase()} ${sectionDrawerData.course.number}`}
        >
          <div className="space-y-2">
            {sectionDrawerData.sections.map((sec, i) => (
              <div key={i} className="border rounded p-2 flex justify-between items-center">
                <div>
                  <span className="font-semibold">Section {sec.section.toUpperCase()}</span>
                  {sec.sfuData && sec.sfuData.info && sec.sfuData.info.title && (
                    <span className="ml-2 text-xs text-gray-600">{sec.sfuData.info.title}</span>
                  )}
                </div>
                <button
                  onClick={() => {
                    const key = getCourseCacheKey(sectionDrawerData.course);
                    setSelectedSections((prev) => ({
                      ...prev,
                      [key]: { ...sectionDrawerData.course, ...sec },
                    }));
                    setIsSectionDrawerOpen(false);
                    handleComputeCombinations();
                  }}
                  className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Select
                </button>
              </div>
            ))}
            <div className="border rounded p-2 text-center">
              <button
                onClick={() => {
                  const key = getCourseCacheKey(sectionDrawerData.course);
                  setSelectedSections((prev) => {
                    const newSelections = { ...prev };
                    delete newSelections[key];
                    return newSelections;
                  });
                  setIsSectionDrawerOpen(false);
                  handleComputeCombinations();
                }}
                className="mt-2 px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Use All Sections
              </button>
            </div>
          </div>
        </SectionsDrawer>
      )}
    </div>
  );
}

function getCourseCacheKey(course) {
  const dept = course.dept.toUpperCase();
  const number = course.number;
  const section = course.section ? course.section.toUpperCase() : "DEFAULT";
  return `${dept}_${number}_${section}`;
}

export default SchedulePage;
